import { mapActions, mapGetters } from 'vuex'
import KPI from '@/components/KPI/KPI.vue'
import { isBigQueryConfigured } from '@/views/Director/director.helpers'

export default {
  name: 'DirectorBigQueryView',
  props: {
    contentplanId: {
      type: Number,
      required: true
    },
    workspaceId: {
      type: Number,
      required: true
    },
    contentplanType: {
      type: String,
      default: 'managed'
    },
    datesFilter: {
      type: Array,
      required: true
    },
    sharingKey: {
      type: String,
      default: ''
    },
    isSharedView: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      bigQueryAssists: {
        items: [],
        journeys: [],
        loading: false,
        loaded: false,
        error: '',
        totalTransactions: 0,
        totalRevenue: 0,
        assistedTransactions: 0,
        assistedRevenue: 0
      },
      attributionWindowDays: 90,
      currency: 'EUR'
    }
  },
  computed: {
    ...mapGetters([
      'integrations'
    ]),
    analyticsIntegration () {
      return this.integrations?.find(item => item.type === 'ga-4') || {}
    },
    bigQueryEnabled () {
      return isBigQueryConfigured(this.analyticsIntegration)
    },
    isEcommerceType () {
      return this.contentplanType === 'ecommerce'
    },
    summaryText () {
      if (this.isEcommerceType) return `This shows purchases completed within ${this.attributionWindowDays} days after a ContentGecko pageview.`
      return `This shows conversions completed within ${this.attributionWindowDays} days after a ContentGecko pageview.`
    },
    totalCountLabel () {
      return this.isEcommerceType ? 'Total Sales' : 'Total Conversions'
    },
    assistedCountLabel () {
      return this.isEcommerceType ? 'Assisted Sales' : 'Assisted Conversions'
    },
    totalValueLabel () {
      return this.isEcommerceType ? `Total Revenue (${this.currency})` : `Total Conversion Value (${this.currency})`
    },
    assistedValueLabel () {
      return this.isEcommerceType ? `Assisted Revenue (${this.currency})` : `Assisted Conversion Value (${this.currency})`
    },
    tableHeaders () {
      const countLabel = this.isEcommerceType ? 'Attributed sales share' : 'Attributed conversion share'
      const valueLabel = this.isEcommerceType ? 'Attributed revenue' : 'Attributed conversion value'
      return [
        { text: 'Page URL', value: 'pageUrl', align: 'start' },
        { text: countLabel, value: 'transactions', align: 'end' },
        { text: `${valueLabel} (${this.currency})`, value: 'revenue', align: 'end' }
      ]
    },
    bigQueryAssistsData () {
      if (!Array.isArray(this.bigQueryAssists.items)) return []
      return this.bigQueryAssists.items
    },
    bigQueryJourneysData () {
      if (!Array.isArray(this.bigQueryAssists.journeys)) return []
      return this.bigQueryAssists.journeys
    }
  },
  watch: {
    datesFilter: {
      deep: true,
      handler () {
        this.loadBigQueryAssists()
      }
    }
  },
  methods: {
    ...mapActions([
      'getBigQueryContentAttribution'
    ]),
    hasValidDateRange () {
      return Array.isArray(this.datesFilter) && this.datesFilter.length === 2 && this.datesFilter[0] && this.datesFilter[1]
    },
    resetBigQueryAssists () {
      this.bigQueryAssists.items = []
      this.bigQueryAssists.journeys = []
      this.bigQueryAssists.loaded = false
      this.bigQueryAssists.error = ''
      this.bigQueryAssists.totalTransactions = 0
      this.bigQueryAssists.totalRevenue = 0
      this.bigQueryAssists.assistedTransactions = 0
      this.bigQueryAssists.assistedRevenue = 0
    },
    formatCurrency (value) {
      const num = parseFloat(value) || 0
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(num)
    },
    formatAttributionShare (value) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(parseFloat(value) || 0)
    },
    formatDateTime (timestampMicros) {
      const date = new Date(Number(timestampMicros) / 1000)
      if (Number.isNaN(date.getTime())) return ''
      return new Intl.DateTimeFormat('en-GB', {
        dateStyle: 'medium',
        timeStyle: 'medium'
      }).format(date)
    },
    formatElapsedTime (secondsInput) {
      const seconds = Math.max(0, parseInt(secondsInput, 10) || 0)
      const days = Math.floor(seconds / 86400)
      const hours = Math.floor((seconds % 86400) / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      if (days) return `${days}d ${hours}h before purchase`
      if (hours) return `${hours}h ${minutes}m before purchase`
      return `${minutes}m before purchase`
    },
    async loadBigQueryAssists () {
      if (!this.hasValidDateRange()) return
      if (!this.bigQueryEnabled || (this.isSharedView && !this.sharingKey)) {
        this.resetBigQueryAssists()
        return
      }

      try {
        this.bigQueryAssists.loading = true
        this.bigQueryAssists.loaded = false
        this.bigQueryAssists.error = ''

        const assistPayload = {
          contentplanId: this.contentplanId,
          startDate: this.datesFilter[0],
          endDate: this.datesFilter[1],
          onlyContentGeckoPages: true,
          calculation: 'blogAssist',
          calculationOptions: {
            limit: 50,
            journeyLimit: 500,
            attributionWindowDays: this.attributionWindowDays
          }
        }

        if (this.sharingKey) assistPayload.key = this.sharingKey

        const assistResponse = await this.getBigQueryContentAttribution(assistPayload)

        const summary = assistResponse?.summary || {}
        this.bigQueryAssists.totalTransactions = parseInt(summary.total_transactions, 10) || 0
        this.bigQueryAssists.totalRevenue = parseFloat(summary.total_revenue) || 0
        this.bigQueryAssists.assistedTransactions = parseInt(summary.transactions, 10) || 0
        this.bigQueryAssists.assistedRevenue = parseFloat(summary.revenue) || 0

        const pageRows = assistResponse?.pages || []
        this.bigQueryAssists.items = pageRows.map(row => ({
          pageUrl: row.page_url,
          transactions: parseFloat(row.transactions) || 0,
          revenue: parseFloat(row.revenue) || 0
        }))

        const journeyRows = assistResponse?.journeys || []
        this.bigQueryAssists.journeys = journeyRows.map(row => ({
          transactionId: row.transaction_id,
          purchaseTimestamp: row.purchase_timestamp,
          revenue: parseFloat(row.revenue) || 0,
          pageviewCount: parseInt(row.pageview_count, 10) || 0,
          attributedPageCount: parseInt(row.attributed_page_count, 10) || 0,
          pageviews: row.pageviews.map(pageview => ({
            pageUrl: pageview.page_url,
            viewedAt: pageview.viewed_at,
            secondsBeforePurchase: pageview.seconds_before_purchase
          }))
        }))
        this.bigQueryAssists.loaded = true
      } catch (error) {
        console.error('Failed to fetch BigQuery assisted conversions', error)
        this.bigQueryAssists.error = error?.response?.data?.message || error.message || 'Failed to load assisted conversions.'
      } finally {
        this.bigQueryAssists.loading = false
      }
    }
  },
  async mounted () {
    await this.loadBigQueryAssists()
  },
  components: { KPI }
}
